import { getApiClient } from '@src/shared/types/api-client'
import { SecretCreationResult, SecretEntry, SecretValidationResult } from '@src/tools/key-vault/types/KeyVault'
import { useMutation, useQuery } from '@tanstack/react-query'

const SCOPE = 'secrets'

const secretKeysFactory = {
  names: (keyVaultName: string) => [{ scope: SCOPE, entity: 'names', keyVaultName }] as const,
}

interface CreateSecretsRequest {
  keyVaultName: string
  secrets: SecretEntry[]
}

interface ValidateSecretsRequest {
  keyVaultName: string
  secrets: SecretEntry[]
}

interface SecretListDto {
  secretNames: string[]
}

async function createSecretsFn(request: CreateSecretsRequest): Promise<SecretCreationResult[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<SecretCreationResult[]>(
    `/KeyVaults/${encodeURIComponent(request.keyVaultName)}/Secrets`,
    { secrets: request.secrets },
  )
  return response.data
}

async function getSecretNames(keyVaultName: string): Promise<string[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<SecretListDto>(
    `/KeyVaults/${encodeURIComponent(keyVaultName)}/Secrets`,
  )
  return response.data.secretNames
}

async function validateSecretsFn(request: ValidateSecretsRequest): Promise<SecretValidationResult[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<SecretValidationResult[]>(
    `/KeyVaults/${encodeURIComponent(request.keyVaultName)}/Secrets/Validate`,
    { secrets: request.secrets },
  )
  return response.data
}

export function useCreateSecrets(): [(request: CreateSecretsRequest) => Promise<SecretCreationResult[]>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createSecretsFn,
  })
  return [mutateAsync, isPending]
}

export function useSecretNames(keyVaultName: string | null): [string[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: secretKeysFactory.names(keyVaultName ?? ''),
    queryFn: () => getSecretNames(keyVaultName!),
    enabled: !!keyVaultName,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useValidateSecrets(): [(request: ValidateSecretsRequest) => Promise<SecretValidationResult[]>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: validateSecretsFn,
  })
  return [mutateAsync, isPending]
}
